import {
  getStudentByCode,
  orUndefined,
  type StudentResponse,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { AlertCircleIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import { AutoRefresh } from '@/components/auto-refresh';
import { useBrand } from '@/features/branding/brand-provider';
import { LanguageSelector } from '@/features/locale/components/language-selector';
import {
  CourseCard,
  type StudentCourse,
} from '@/features/student/components/course-card';
import { NotFound } from '@/routes/not-found';

function mapStudentCourses(data: StudentResponse): StudentCourse[] {
  return data.courses.map((c) => ({
    id: c.sessionId,
    courseId: c.courseId,
    curriculumName: data.curriculumName,
    curriculumNameTranslated: data.curriculumNameTranslated,
    courseData: c.courseData,
    status: c.status === 'not_started' ? null : c.status,
    image: c.image,
    ordering: c.ordering,
  }));
}

export function TraineeDashboard() {
  // The `id` param is the trainee's personal code.
  const { id } = useParams<{ id: string }>();
  const t = useTranslations('me');
  const { brand: site } = useBrand();

  const { data: studentData, isLoading } = useSWR(
    id ? ['student', id] : null,
    // The backend validates the code and returns student data.
    () => orUndefined(getStudentByCode(id ?? ''), [400, 401, 404]),
  );

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex h-dvh items-center justify-center">
        Loading…
      </div>
    );
  }

  if (!studentData) {
    return <NotFound />;
  }

  const courses = mapStudentCourses(studentData);
  const unfinished = courses.reduce(
    (sum, session) => sum + (session.status === 'passed' ? 0 : 1),
    0,
  );

  return (
    <div
      className="flex h-full flex-col items-stretch justify-stretch"
      data-testid="trainee-dashboard"
    >
      <AutoRefresh />
      {site.poster && (
        <div className="fixed top-0 right-0 bottom-0 left-0 -z-10">
          <img
            src={site.poster}
            alt="poster"
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <header className="bg-background/80 h-96 max-h-[50dvh] md:-mb-32">
        <div className="mx-auto grid h-96 max-w-screen-2xl grid-cols-1 gap-4 px-8 md:grid-cols-3">
          <div className="order-2 flex flex-row items-center justify-center gap-8 pt-8 md:order-1 md:justify-start md:pt-0">
            <img
              src={site.logo}
              className="h-full max-h-28 max-w-28 object-contain"
              alt={site.name}
              data-testid="site-logo"
            />
            <div>
              <h1 className="text-2xl font-semibold" data-testid="site-name">
                {site.name}
              </h1>
              <h3 className="text-xl" data-testid="group-name">
                {studentData.groupName}
              </h3>
            </div>
          </div>

          <div className="order-3 md:order-2" />

          <div className="order-2 flex flex-col-reverse md:order-3 md:h-full md:flex-col">
            <div className="flex flex-row items-center justify-center gap-4 py-4 md:justify-end">
              <Button asChild data-testid="exit-button">
                <Link to={`/${site.slug}/login`}>{t('exit')}</Link>
              </Button>
            </div>
            <div className="flex flex-grow flex-col items-center justify-center md:items-end">
              <div className="md:mb-[4rem]" data-testid="progress-status">
                <h1 className="mb-2 text-2xl font-semibold">
                  {unfinished === 0 ? t('status.done') : t('status.not_done')}
                </h1>
                {courses.length === 0 ? (
                  <p className="text-lg" data-testid="no-courses-message">
                    <AlertCircleIcon className="inline" />{' '}
                    {t('status.no_courses')}
                  </p>
                ) : unfinished === 0 ? (
                  <p className="text-lg" data-testid="all-done-message">
                    <CheckIcon className="inline" />{' '}
                    {t('status.all_courses_done')}
                  </p>
                ) : (
                  <p className="text-lg" data-testid="unfinished-message">
                    <InfoIcon className="inline" />{' '}
                    {t('status.unfinished_courses', { unfinished })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="mx-auto mb-4 max-w-screen-2xl grow">
        <div className="flex flex-wrap justify-center gap-4">
          {courses?.map((course, index) => (
            <CourseCard key={course.id ?? `course-${index}`} course={course} />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center p-4">
        <LanguageSelector />
      </div>
    </div>
  );
}
